import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Sparkles, Heart, Leaf, Sun } from "lucide-react";

export const Route = createFileRoute("/brand-colors")({
  component: BrandColorsPage,
  head: () => ({
    meta: [
      { title: "Brand Colors — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Preview the Recovery Trauma™ brand palette: Soft Gold, Warm Ivory, Sage Green, and Soft Lavender across UI elements.",
      },
      { property: "og:title", content: "Brand Colors — Recovery Trauma™" },
      { property: "og:description", content: "Preview the Recovery Trauma™ brand palette: Soft Gold, Warm Ivory, Sage Green, and Soft Lavender across UI elements." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/brand-colors" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/brand-colors" }],
  }),
});

type Swatch = {
  name: string;
  hint: string;
  bg: string;
  fg: string;
  cssVar: string;
};

const SWATCHES: Swatch[] = [
  {
    name: "Soft Gold",
    hint: "Primary accent — buttons, active nav, highlights",
    bg: "bg-[var(--gold)]",
    fg: "text-[oklch(0.22_0.04_60)]",
    cssVar: "--gold",
  },
  {
    name: "Gold Deep",
    hint: "Text on light surfaces, hover states",
    bg: "bg-[var(--gold-deep)]",
    fg: "text-[oklch(0.98_0.02_90)]",
    cssVar: "--gold-deep",
  },
  {
    name: "Gold Light",
    hint: "Soft fills, chips, gradient highlights",
    bg: "bg-[var(--gold-light)]",
    fg: "text-[oklch(0.30_0.05_60)]",
    cssVar: "--gold-light",
  },
  {
    name: "Warm Ivory",
    hint: "Page background — calm and grounded",
    bg: "bg-[var(--cream)]",
    fg: "text-[oklch(0.30_0.04_60)]",
    cssVar: "--cream",
  },
  {
    name: "Sage Green",
    hint: "Secondary accent — recovery & growth",
    bg: "bg-[var(--sage)]",
    fg: "text-[oklch(0.22_0.04_145)]",
    cssVar: "--sage",
  },
  {
    name: "Sage Deep",
    hint: "Sage on light, supportive buttons",
    bg: "bg-[var(--sage-deep)]",
    fg: "text-[oklch(0.98_0.02_145)]",
    cssVar: "--sage-deep",
  },
  {
    name: "Soft Lavender",
    hint: "Tertiary accent — gentle highlights",
    bg: "bg-[var(--lavender)]",
    fg: "text-[oklch(0.22_0.05_305)]",
    cssVar: "--lavender",
  },
  {
    name: "Lavender Deep",
    hint: "Lavender text & subtle dividers",
    bg: "bg-[var(--lavender-deep)]",
    fg: "text-[oklch(0.98_0.01_305)]",
    cssVar: "--lavender-deep",
  },
];

function BrandColorsPage() {
  return (
    <div className="min-h-dvh bg-background py-16">
      <div className="mx-auto max-w-6xl px-6">
        <header className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-light/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            <Sparkles className="h-3.5 w-3.5" /> Brand Palette
          </div>
          <h1 className="font-display text-4xl text-foreground">
            Recovery Trauma™ Colors
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Soft Gold leads, Warm Ivory grounds, Sage Green supports, and Soft
            Lavender accents — applied consistently across navigation, buttons,
            and highlights.
          </p>
        </header>

        {/* Swatch grid */}
        <section className="mb-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SWATCHES.map((s) => (
            <div
              key={s.name}
              className="card-premium overflow-hidden"
            >
              <div className={`${s.bg} ${s.fg} flex h-32 items-end p-4`}>
                <span className="font-display text-lg">{s.name}</span>
              </div>
              <div className="p-4">
                <p className="text-sm text-foreground">{s.hint}</p>
                <code className="mt-2 inline-block rounded bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                  var({s.cssVar})
                </code>
              </div>
            </div>
          ))}
        </section>

        {/* In-context examples */}
        <section className="mb-12">
          <h2 className="mb-5 font-display text-2xl text-foreground">
            In context
          </h2>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Buttons */}
            <div className="card-premium p-6">
              <h3 className="mb-4 font-display text-lg text-foreground">
                Buttons
              </h3>
              <div className="flex flex-wrap gap-3">
                <button className="btn-gold rounded-full px-5 py-2.5 text-sm">
                  <Sparkles className="h-4 w-4" /> Soft Gold CTA
                </button>
                <button className="btn-gold-soft">
                  <Heart className="h-4 w-4" /> Gold Soft
                </button>
                <button className="btn-gold-outline">Outline</button>
                <Button>Primary</Button>
                <Button variant="secondary">Sage Secondary</Button>
              </div>
            </div>

            {/* Icon chips */}
            <div className="card-premium p-6">
              <h3 className="mb-4 font-display text-lg text-foreground">
                Icon chips
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <span className="icon-chip">
                  <Sun />
                </span>
                <span className="icon-chip icon-chip-active">
                  <Sparkles />
                </span>
                <span className="icon-chip icon-chip-lg">
                  <Heart />
                </span>
                <span
                  className="icon-chip"
                  style={{
                    background:
                      "color-mix(in oklab, var(--sage) 22%, transparent)",
                    color: "var(--sage-deep)",
                  }}
                >
                  <Leaf />
                </span>
                <span
                  className="icon-chip"
                  style={{
                    background:
                      "color-mix(in oklab, var(--lavender) 25%, transparent)",
                    color: "var(--lavender-deep)",
                  }}
                >
                  <Sparkles />
                </span>
              </div>
            </div>

            {/* Surface stack */}
            <div className="card-premium overflow-hidden">
              <div className="bg-[var(--cream)] p-6">
                <h3 className="font-display text-lg text-foreground">
                  Warm Ivory surface
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Calm background for cards, hero panels, and reading surfaces.
                </p>
                <div className="mt-4 flex gap-2">
                  <span className="rounded-full bg-[var(--gold-light)] px-3 py-1 text-xs font-medium text-[var(--gold-deep)]">
                    Gold tag
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      background:
                        "color-mix(in oklab, var(--sage) 28%, transparent)",
                      color: "var(--sage-deep)",
                    }}
                  >
                    Sage tag
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      background:
                        "color-mix(in oklab, var(--lavender) 30%, transparent)",
                      color: "var(--lavender-deep)",
                    }}
                  >
                    Lavender tag
                  </span>
                </div>
              </div>
            </div>

            {/* Active nav preview */}
            <div className="card-premium p-6">
              <h3 className="mb-4 font-display text-lg text-foreground">
                Active navigation
              </h3>
              <div className="flex items-end gap-2">
                <div className="nav-pill">
                  <span className="nav-pill__chip">
                    <Sun />
                  </span>
                  <span className="nav-pill__label">Home</span>
                </div>
                <div className="nav-pill" data-active="true">
                  <span className="nav-pill__chip">
                    <Sparkles />
                  </span>
                  <span className="nav-pill__label">Active</span>
                </div>
                <div className="nav-pill">
                  <span className="nav-pill__chip">
                    <Heart />
                  </span>
                  <span className="nav-pill__label">Care</span>
                </div>
                <div className="nav-pill">
                  <span className="nav-pill__chip">
                    <Leaf />
                  </span>
                  <span className="nav-pill__label">Grow</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
